import { TaskParams } from '~/components/task-forms/BaseTaskForm'
import { TaskSubmitPayload } from '~/services/taskService'

/**
 * Convert TaskParams (with protobuf AgentRequest) to simplified JSON payload.
 * Frontend sends simple JSON → Backend builds protobuf.
 *
 * This is a temporary bridge until TaskForm is refactored to build JSON directly.
 */
export function buildSimplifiedTaskRequest(task: TaskParams): TaskSubmitPayload['request'] {
  const request = task.request

  // Extract simple parameters from protobuf request
  const simplified: TaskSubmitPayload['request'] = {
    command_type: request.command?.commandType || 0,
    command_string: request.command?.commandString
  }

  if (request.platform) {
    simplified.platform = request.platform
  }
  if (request.toPlatform) {
    simplified.to_platform = request.toPlatform
  }

  // Extract execution_mode and verification_mode from command
  if (request.command?.executionMode !== undefined && request.command.executionMode !== 0) {
    simplified.execution_mode = request.command.executionMode
  }
  if (request.command?.verificationMode !== undefined && request.command.verificationMode !== 0) {
    simplified.verification_mode = request.command.verificationMode
  }

  // Extract images from command
  if (request.command?.images && request.command.images.length > 0) {
    simplified.images = request.command.images.map(img => ({
      image_extension: img.imageExtension || 'png',
      image_base64_content: img.imageBase64Content || ''
    }))
  }

  // Extract context_upload from command (metadata files)
  if (request.command?.contextUpload) {
    const cu = request.command.contextUpload
    simplified.context_upload = {
      instructions: cu.instructions || '',
      files:
        cu.files?.map(file => ({
          file_name: file.fileName || '',
          file_s3_path: file.fileS3Path || ''
        })) || []
    }

    // Add directories if present
    if (cu.directories && cu.directories.length > 0) {
      simplified.context_upload.directories = cu.directories.map(dir => ({
        directory_name: dir.directoryName || '',
        directory_s3_path: dir.directoryS3Path || ''
      }))
    }
  }

  // Extract code context (single repo)
  if (request.metadata?.codeContext) {
    const cc = request.metadata.codeContext
    simplified.code_context = {
      conn_type: cc.connType || '',
      org_name: cc.orgName,
      repo_name: cc.repoName,
      branch_name: cc.branchName,
      s3_tar_file_path: cc.s3TarFilePath
    }
  }

  // Extract multi-repo code context
  if (request.metadata?.multiRepoCodeContext && request.metadata.multiRepoCodeContext.length > 0) {
    simplified.multi_repo_code_context = request.metadata.multiRepoCodeContext.map(cc => ({
      conn_type: cc.connType || '',
      org_name: cc.orgName,
      repo_name: cc.repoName,
      branch_name: cc.branchName,
      s3_tar_file_path: cc.s3TarFilePath
    }))
  }

  // Extract auxiliary code context (e.g., MuleSoft code for refactoring)
  if (request.metadata?.auxiliaryCodeContext && request.metadata.auxiliaryCodeContext.length > 0) {
    simplified.auxiliary_code_context = request.metadata.auxiliaryCodeContext.map(cc => ({
      conn_type: cc.connType || '',
      org_name: cc.orgName,
      repo_name: cc.repoName,
      branch_name: cc.branchName,
      s3_tar_file_path: cc.s3TarFilePath
    }))
  }

  // Extract runtime settings
  if (request.runtimeSettings) {
    const rs = request.runtimeSettings
    simplified.runtime_settings = {
      java_version: rs.javaVersion,
      maven_version: rs.mavenVersion,
      mule_runtime_version: rs.muleRuntimeVersion,
      settings_xml_raw_txt: rs.settingsXmlRawTxt,
      settings_security_xml_raw_txt: rs.settingsSecurityXmlRawTxt
    }
  }

  // Extract old runtime settings (for MULE_VERSION_UPGRADE)
  if (request.oldRuntimeSettings) {
    const ors = request.oldRuntimeSettings
    simplified.old_runtime_settings = {
      java_version: ors.javaVersion,
      maven_version: ors.mavenVersion,
      mule_runtime_version: ors.muleRuntimeVersion
    }
  }

  // Extract source/output examples
  if (request.sourceOutputExamples && request.sourceOutputExamples.length > 0) {
    simplified.source_output_examples = request.sourceOutputExamples.map(soe => ({
      source_data: soe.sourceData,
      output_data: soe.outputData,
      source_data_type: sourceOutputExampleTypeToString(soe.sourceDataType),

      // EDI schema fields
      input_edi_transaction_type: soe.inputEdiSchemaType?.transactionType,
      input_edi_transaction_version: soe.inputEdiSchemaType?.transactionVersion,
      output_edi_transaction_type: soe.outputEdiSchemaType?.transactionType,
      output_edi_transaction_version: soe.outputEdiSchemaType?.transactionVersion,

      // EDIFACT schema fields
      input_edifact_version: soe.inputEdiFactSchemaType?.version,
      input_edifact_message: soe.inputEdiFactSchemaType?.message,
      output_edifact_version: soe.outputEdiFactSchemaType?.version,
      output_edifact_message: soe.outputEdiFactSchemaType?.message,

      // HL7 schema fields
      input_hl7_version: soe.inputHl7SchemaType?.version,
      input_hl7_message: soe.inputHl7SchemaType?.message,
      input_hl7_segment_terminator: soe.inputHl7SchemaType?.segmentTerminator,
      input_hl7_schema_type: soe.inputHl7SchemaType?.schemaType,
      output_hl7_version: soe.outputHl7SchemaType?.version,
      output_hl7_message: soe.outputHl7SchemaType?.message,
      output_hl7_segment_terminator: soe.outputHl7SchemaType?.segmentTerminator,
      output_hl7_schema_type: soe.outputHl7SchemaType?.schemaType
    }))
  }

  // Extract mapping sheet
  if (request.mappingSheet && request.mappingSheet.length > 0) {
    // Send mapping sheets with base64-encoded Excel files
    simplified.mapping_sheet = request.mappingSheet.map(sheet => ({
      id: sheet.id,
      base64_encoded_xlsx: sheet.base64EncodedXlsx || '',
      csv: sheet.csv || ''
    }))
  }

  // Extract other fields
  if (request.userNotes) simplified.user_notes = request.userNotes
  if (request.inputOutputNotes) simplified.input_output_notes = request.inputOutputNotes
  if (request.vmArgs) simplified.vm_args = request.vmArgs
  if (request.munitNotes) simplified.munit_notes = request.munitNotes
  if (request.munitEnableCoverage !== undefined)
    simplified.munit_enable_coverage = request.munitEnableCoverage
  if (request.munitNumScenarios !== undefined)
    simplified.munit_num_scenarios = request.munitNumScenarios
  if (request.generateFlowDiag !== undefined)
    simplified.generate_flow_diag = request.generateFlowDiag
  if (request.generateSeqDiag !== undefined) simplified.generate_seq_diag = request.generateSeqDiag
  if (request.runTests !== undefined) simplified.run_tests = request.runTests
  if (request.runDeploy !== undefined) simplified.run_deploy = request.runDeploy
  if (request.enableWebSearch !== undefined) simplified.enable_web_search = request.enableWebSearch

  // Extract DWL to input/output example
  if (request.dwlToInputOutputExample) {
    const dwl = request.dwlToInputOutputExample
    simplified.dwl_to_input_output_example = {
      command_string: dwl.commandString || '',
      example_type: sourceOutputExampleTypeToString(dwl.exampleType)
    }
  }
  // Extract migration doc input
  if (request.migrationDocInput) {
    const mdi = request.migrationDocInput
    simplified.migration_doc_input = {
      platform: mdi.platform || 0,
      endpoint_desc: mdi.endpointDesc
    }
    console.log(
      '[TaskRequestBuilder] Extracted migration_doc_input:',
      simplified.migration_doc_input
    )
  }

  if (request.integrationTestConfig) {
    const config = request.integrationTestConfig
    simplified.integration_test_config = {
      flows:
        config.flows?.map(flow => ({
          flow_id: flow.flowId || '',
          examples:
            flow.examples?.map(ex => ({
              input_example: ex.inputExample || '',
              output_example: ex.outputExample || ''
            })) || []
        })) || [],
      enable_comparison_testing: config.enableComparisonTesting || false
    }
  }

  console.log('[TaskRequestBuilder] Final simplified request:', JSON.stringify(simplified, null, 2))
  return simplified
}

/**
 * Convert protobuf enum to string representation
 */
function sourceOutputExampleTypeToString(type: number | undefined): string {
  if (!type) return 'DATA_TYPE_JSON'

  // Map protobuf enum values to string names
  // IMPORTANT: These must match the enum values in agents.proto
  const typeMap: Record<number, string> = {
    0: 'DATA_TYPE_UNKNOWN',
    1: 'DATA_TYPE_XML',
    2: 'DATA_TYPE_YAML',
    3: 'DATA_TYPE_JSON',
    4: 'DATA_TYPE_CSV',
    5: 'DATA_TYPE_MULTIPART',
    6: 'DATA_TYPE_TEXT',
    7: 'DATA_TYPE_EDI',
    8: 'DATA_TYPE_EDIFACT',
    9: 'DATA_TYPE_HL7'
  }

  return typeMap[type] || 'DATA_TYPE_JSON'
}
